import { useState, useEffect, FormEvent } from 'react';

import { Form, Formik } from 'formik';
import * as Yup from 'yup';

import { Input, Select, SelectWithSearch, SubmitButton, OutlinedButton, TextArea } from 'components/UIComponents';
import { StyledDialogActions as DialogActions } from 'components/base/BaseModal/styledDialogActions';
import { StyledDialogContent as DialogContent } from 'components/base/BaseModal/styledDialogContent';

import { TestObjectiveAPI } from 'api/testObjectiveAPI';

import { getProductOptions } from 'helpers/collections';

import { ITestObjectiveForm, ITestObjectiveEditForm } from 'interfaces/TestObjective/ITestObjective';

import RESPONSE_CODES from 'constants/responseCodes';

interface TestObjectiveFormProps {
  onModalClose: () => void;
  successAction: () => void;
  editObjectiveId: number;
  editValues: { briefDescription: string; longDescription: string };
}

const currentYear = new Date().getFullYear();
const YEAR_RANGE_START = 3;
const YEAR_RANGE_END = 100;
const yearOptions = Array.from({ length: YEAR_RANGE_START + YEAR_RANGE_END + 1 }, (_, i) => {
  const year = String(currentYear - YEAR_RANGE_START + i);
  return { key: year, value: year };
});

const createSchema = Yup.object({
  productId: Yup.number().required('Product is required'),
  year: Yup.string().required('Year is required'),
  number: Yup.number().required('Number is required').integer('Must be a whole number'),
  briefDescription: Yup.string().required('Short version is required'),
  longDescription: Yup.string().required('Long version is required'),
});

const editSchema = Yup.object({
  briefDescription: Yup.string().required('Short version is required'),
  longDescription: Yup.string().required('Long version is required'),
});

const TestObjectiveForm = ({
  onModalClose,
  successAction,
  editObjectiveId,
  editValues,
}: TestObjectiveFormProps) => {
  const isEdit = !!editObjectiveId;

  const [initialValues, setInitialValues] = useState<ITestObjectiveForm>({
    productId: '',
    year: String(currentYear),
    number: '',
    briefDescription: '',
    longDescription: '',
  });

  useEffect(() => {
    if (isEdit) {
      setInitialValues((prev) => ({
        ...prev,
        briefDescription: editValues.briefDescription,
        longDescription: editValues.longDescription,
      }));
    } else {
      setInitialValues({
        productId: '',
        year: String(currentYear),
        number: '',
        briefDescription: '',
        longDescription: '',
      });
    }
  }, [editObjectiveId, editValues, isEdit]);

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={isEdit ? editSchema : createSchema}
      validateOnBlur
      enableReinitialize
      onSubmit={async (values) => {
        let response;

        if (isEdit) {
          const editData: ITestObjectiveEditForm = {
            briefDescription: values.briefDescription,
            longDescription: values.longDescription,
          };
          response = await TestObjectiveAPI.update(editObjectiveId, editData);
        } else {
          response = await TestObjectiveAPI.create(values as ITestObjectiveForm);
        }

        if (response?.status !== RESPONSE_CODES.UNPROCESSABLE_ENTITY) {
          onModalClose();
          successAction();
        }
      }}
    >
      {({ errors, handleSubmit, dirty }) => (
        <Form>
          <DialogContent>
            {!isEdit && (
              <>
                <SelectWithSearch
                  name="productId"
                  label="Product"
                  getOptionsList={getProductOptions}
                  required
                />
                <Select
                  name="year"
                  label="Year"
                  options={yearOptions}
                  required
                  allowEmpty={false}
                />
                <Input name="number" label="Number" type="number" required />
              </>
            )}
            <TextArea name="briefDescription" label="Short Version" />
            <TextArea name="longDescription" label="Long Version" />
          </DialogContent>
          <DialogActions>
            <OutlinedButton onClick={onModalClose}>Cancel</OutlinedButton>
            <SubmitButton
              disabled={!dirty || !!Object.values(errors).length}
              onClick={(values: FormEvent<HTMLFormElement>) => handleSubmit(values)}
            >
              {isEdit ? 'Save' : 'Add'}
            </SubmitButton>
          </DialogActions>
        </Form>
      )}
    </Formik>
  );
};

export default TestObjectiveForm;
